import { FC, useEffect } from 'react';

import { CurrencyConnected } from '@pp/core/src/components/currency/CurrencyConnected';
import { AutoplayView } from '@pp/core/src/components/game/autoplayV2/AutoplayView';
import { AutoplayBetInfo } from '@pp/core/src/components/game/autoplayV2/betInfo/AutoplayBetInfo';
import { GameButtonProps } from '@pp/core/src/components/game/gameButton/gameButton';
import { Logger } from '@pp/core/src/logger/Logger';
import { L10nText } from '@pp/core/src/services/uiTranslations/reactBindings/L10nText';
import { useCoreDispatch } from '@pp/core/src/store/CoreStore';
import { useBetSlice } from '@pp/core/src/store/slices/bets/selectors';
import { betActions } from '@pp/core/src/store/slices/bets/slice';
import { BetType } from '@pp/core/src/store/slices/bets/types';
import { useConfigSlice } from '@pp/core/src/store/slices/config/selectors';
import { useGameStateSlice } from '@pp/core/src/store/slices/game/selectors';
import { toastActions } from '@pp/core/src/store/slices/toast/slice';

import { Bets } from '../../../config/bets';
import { useAutoCashoutSlice } from '../../../store/slices/autocashout/selectors';
import { useCrashAutoplaySlice } from '../../../store/slices/crashAutoplay/selectors';
import { crashAutoplayActions } from '../../../store/slices/crashAutoplay/slice';
import { useHighflyerStateSlice } from '../../../store/slices/highflyerState/selectors';
import { BalanceFooter } from '../../balanceFooter/BalanceFooter';
import { Drawer } from '../../drawer/Drawer';
import { useIsBetConfirmedConnected } from '../../gameGrid/hooks/useIsBetConfirmedConnected';
import { useOnBetCallbackConnected } from '../../gameGrid/hooks/useOnBetCallbackConnected';
import { useOnCancelCallbackConnected } from '../../gameGrid/hooks/useOnCancelCallbackConnected';
import { useValidBetInputValue } from '../../gameGrid/hooks/useValidBetInputValue';

import { CrashAutoplayActiveControlsConnected } from './CrashAutoplayActiveControlsConnected';
import { CrashAutoplayInactiveControlsConnected } from './CrashAutoplayInactiveControlsConnected';

import styles from './CrashAutoplayModal.module.css';

export interface CrashAutoplayModalConnectedProps extends Omit<GameButtonProps, 'icon'> {
  betId: Bets;
}
export const CrashAutoplayModalConnectedV2: FC<CrashAutoplayModalConnectedProps> = ({ betId }) => {
  const dispatch = useCoreDispatch();
  const currentGameId = useGameStateSlice.gameId();
  const currentBet = useBetSlice.bets();
  const earlybets = useHighflyerStateSlice.getEarlyBetAmount(betId);
  const isBetsOpen = useGameStateSlice.isBetsOpenStatus();
  const isInProgress = useCrashAutoplaySlice.isInProgress(betId);
  const { betInputAmount } = useValidBetInputValue(betId);
  const isOpen = useCrashAutoplaySlice.isOpen(betId);
  const onBet = useOnBetCallbackConnected();
  const isBetConfirmed = useIsBetConfirmedConnected(betId);
  const isInfitniteAutoPlay = useConfigSlice.isInfiniteAutoPlayEnabled();
  const onCancel = useOnCancelCallbackConnected(betId);
  const isInDealingInProgress = useHighflyerStateSlice.isDealingInProgress(betId);
  const { isEnabled, cashoutMultiplier } = useAutoCashoutSlice.getAutoCashoutObj(betId);
  const enableInfiniteAutoplay = useCrashAutoplaySlice.getInfiniteAutoplayEnabled(betId);

  useEffect(() => {
    if (isEnabled || betInputAmount) {
      dispatch(crashAutoplayActions.setIsThisRoundOnly({ bestSpot: Number(betId), thisRoundonly: false }));
    }
  }, [isEnabled, cashoutMultiplier, betInputAmount, dispatch, betId]);

  const toggleOpen = (): void => {
    dispatch(crashAutoplayActions.toggleOpen(betId));
    Logger.logEvent('click', 'autoplay', Date.now(), 1, '', false);
  };

  const launchAutoplayV2 = (rounds: number): void => {
    const betAmount =
      currentBet && currentBet.betSpots[betId]?.value > 0 ? currentBet.betSpots[betId].value : earlybets || 0;

    if (isInProgress && betAmount <= 0) {
      return;
    }

    if (!isInDealingInProgress) {
      dispatch(betActions.clearBet(betId)); // only single bet should set accordingly the auto play kept bet id
    }

    onBet(betId, betInputAmount);

    if (isBetConfirmed && currentBet) {
      const newBet: BetType = { betSpots: {} };
      newBet.betSpots[betId] = currentBet.betSpots[betId].value > 0 ? currentBet.betSpots[betId] : { value: earlybets };
      dispatch(
        crashAutoplayActions.saveBet({
          bestSpot: betId,
          savedBet: { betType: newBet, isEnable: isEnabled, value: cashoutMultiplier },
          gameId: currentGameId,
        })
      );
    } else {
      dispatch(
        crashAutoplayActions.saveBet({
          bestSpot: betId,
          savedBet: {
            betType: { betSpots: { [betId]: { value: betInputAmount } } },
            isEnable: isEnabled,
            value: cashoutMultiplier,
          },
          gameId: currentGameId,
        })
      );
    }

    if (enableInfiniteAutoplay) {
      dispatch(crashAutoplayActions.updateRoundCount({ bestSpot: betId, count: rounds }));
    } else {
      dispatch(crashAutoplayActions.setRounds({ betsSpot: betId, value: rounds }));
      toggleOpen();
    }

    if (!isInProgress) {
      dispatch(crashAutoplayActions.start({ midRound: !isBetsOpen, bestSpot: betId }));
      Logger.logEvent('autoplay_start', { additional: rounds.toString() });
    } else if (!isInfitniteAutoPlay) {
      dispatch(toastActions.createToast('AUTOPLAY_ROUNDS_MODIFIED'));
    }
  };

  useEffect(() => {
    if (enableInfiniteAutoplay) {
      dispatch(toastActions.createToast('TOAST_AUTOPLAY_INFINITE'));
      launchAutoplayV2(-1);
    } else if (enableInfiniteAutoplay == false) {
      // checking == false becasue !null even runing the condition
      dispatch(toastActions.createToast('AUTOPLAY_STOPPED'));
      dispatch(crashAutoplayActions.stop({ betsSpot: betId }));
      dispatch(crashAutoplayActions.setInfiniteAutoplayEnabledOnCalncel({ bestSpot: betId, cancelled: null }));
      onCancel(betId);
    }
  }, [enableInfiniteAutoplay, dispatch]);

  return (
    <Drawer data-testid="autoplay-modal" open={isOpen} onClose={toggleOpen} title={<L10nText tk="AUTOPLAY" />} end>
      <AutoplayView>
        <AutoplayBetInfo totalBet={<CurrencyConnected value={betInputAmount} />} />
        <CrashAutoplayActiveControlsConnected betId={betId} />
        <CrashAutoplayInactiveControlsConnected betId={betId} launchAutoplay={launchAutoplayV2} />
        <BalanceFooter className={styles.autoplay__balanceFooter} />
      </AutoplayView>
    </Drawer>
  );
};
